<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useYardInTransitFeature } from '@satellite/composables/yard/useYardInTransitFeature';
import { useYardTaskPresenter } from '@satellite/composables/useYardTaskPresenter';

import type { Maybe, TaskViewData } from '@nova/core';

const { task } = defineProps<{
  task: Maybe<TaskViewData>;
}>();

const { t } = useI18n();
const { isYardInTransitVisible } = useYardInTransitFeature();

const { isBlocked, isTaskCreated, isTaskInProgress, destinationSpotLabel } = useYardTaskPresenter(
  computed(() => task),
  { i18n: t }
);

/**
 * Created and in-progress are the states the backend blocks, so they are the states that explain
 * themselves. A completed or cancelled task can still carry the tag, and there the block is history.
 */
const isTaskOpen = computed(() => isTaskCreated.value || isTaskInProgress.value);

const isVisible = computed(
  () =>
    isYardInTransitVisible.value &&
    isBlocked.value &&
    isTaskOpen.value &&
    Boolean(destinationSpotLabel.value)
);

const message = computed(() =>
  t('core.yard.messages.blockedUntilSpotIsOpen', { spot: destinationSpotLabel.value })
);
</script>

<template>
  <div
    v-if="isVisible"
    class="d-flex flex-row ga-1 align-center"
    data-testid="yard-task-blocked-banner">
    <v-icon icon="mdi-alert-circle-outline" size="16" class="text--color-warning-100" />
    <span class="typography-body-sm text--color-warning-100">{{ message }}</span>
  </div>
</template>
